import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import { useUpdateVanQuestionnaireStore } from '@/stores/useUpdateVanQuestionnaire';
import {
	MonthlyIncomeInformation as MonthlyIncomeInformationType,
	Maybe
} from '@/graphql/__generated__/van-credits';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface MonthlyIncomeInformationProps {
	data: Maybe<MonthlyIncomeInformationType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	businessSales = 'business_sales',
	salary = 'salary',
	familyContributionsIncome = 'family_contributions_income',
	otherSourcesIncome = 'other_sources_income',
	otherSourcesIncomeDetail = 'other_sources_income_detail'
}

const MonthlyIncomeInformation: React.FC<MonthlyIncomeInformationProps> = ({
	data,
	title,
	isEditing,
	inputErrors
}) => {
	const t = useTranslations('VanCreditRequestQuestionnaire');
	const { monthlyIncome, updateMonthlyIncome } = useUpdateVanQuestionnaireStore();
	const [formData, setFormData] = useState(monthlyIncome as MonthlyIncomeInformationType);

	useEffect(() => {
		setFormData(monthlyIncome);
	}, [monthlyIncome]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateMonthlyIncome({ [key]: value });
	};

	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'businessSales'}
						value={formData['businessSales'] || data?.businessSales || ''}
						onChange={(newValue) => handleChange('businessSales', newValue)}
						error={inputErrors?.[inputErrorMapping.businessSales] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'salary'}
						value={formData['salary'] || data?.salary || ''}
						onChange={(newValue) => handleChange('salary', newValue)}
						error={inputErrors?.[inputErrorMapping.salary] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'familyContributionsIncome'}
						value={formData['familyContributionsIncome'] || data?.familyContributionsIncome || ''}
						onChange={(newValue) => handleChange('familyContributionsIncome', newValue)}
						error={inputErrors?.[inputErrorMapping.familyContributionsIncome] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'otherSourcesIncome'}
						value={
							formData['otherSourcesIncome'] !== null
								? formData['otherSourcesIncome']
								: data?.otherSourcesIncome || ''
						}
						onChange={(newValue) => handleChange('otherSourcesIncome', newValue)}
						error={inputErrors?.[inputErrorMapping.otherSourcesIncome] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'otherSourcesIncomeDetail'}
						value={formData['otherSourcesIncomeDetail'] || data?.otherSourcesIncomeDetail || ''}
						onChange={(newValue) => handleChange('otherSourcesIncomeDetail', newValue)}
						error={inputErrors?.[inputErrorMapping.otherSourcesIncomeDetail] || ''}
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			<div className="flex flex-row w-full">
				<div className="flex flex-row flex-wrap w-full">
					{isEditing ? (
						editableFields()
					) : (
						<>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'businessSales'}
									value={formatValue(data?.businessSales, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput title={'salary'} value={formatValue(data?.salary, t)} />
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'familyContributionsIncome'}
									value={formatValue(data?.familyContributionsIncome, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'otherSourcesIncome'}
									value={formatValue(data?.otherSourcesIncome, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'otherSourcesIncomeDetail'}
									value={formatValue(data?.otherSourcesIncomeDetail, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'totalIncome'}
									value={formatValue(data?.totalIncome, t)}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default MonthlyIncomeInformation;
